/* eslint-disable no-restricted-globals */
import React, { useEffect, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash, faPlus } from "@fortawesome/free-solid-svg-icons";
import { debounce } from "lodash";
import { Descriptions } from "../constants/descriptions";
import ClinvarAccession from "./ClinvarAccession";
import { validationErrors } from "../constants/validationErrors";
import {
  ClinicalSignificanceDescriptionOptions,
  DbOptions,
  ModeofInhertinceOptions,
} from "../constants/form-constants";
import { ClinicalSignificanceFieldsType } from "../types/types";
import HelperText from "../../../components/atoms/HelperText";
import {
  Input,
  FormGroup,
  InputHelperText,
  Select,
  Label,
  Textarea,
} from "../../../components/atoms/Form";
import Article from "../../../components/Publications/models/Article";
import { Loading } from "../../../components/atoms";
import { getPubmedArticles } from "../services/pubmed-ids";
import { silver } from "../../../common/helpers/colors";
import ArticleCitation from "../../../components/Publications/components/ExpandableArticle/ExpandableArticle";

const ClinicalSignificance = (props) => {
  const disabledStyle = {
    pointerEvents: "none",
    // eslint-disable-next-line prettier/prettier
    cursor: "not-allowed",
    // eslint-disable-next-line prettier/prettier
    color: silver,
  };
  const {
    onChageClinicalSign,
    formStatus,
    onClinvarAccesion,
    touched,
    errors,
    handleBlur,
    defaults,
    setErrors,
    setTouched,
    fetchedClinvarAccession,
    recordStatus,
    statusCode,
  } = props;
  const [state, setState] = useState({
    citation: defaults?.citation?.length
      ? defaults.citation
      : [
          {
            db: "",
            id: "",
            url: "",
          },
        ],
    modeOfInheritance: defaults?.modeOfInheritance || "",
    clinicalSignificanceDescription:
      defaults?.clinicalSignificanceDescription || "",
    comment: defaults?.comment || "",
    dateLastEvaluated: defaults?.dateLastEvaluated || "",
    customAssertionScore: defaults?.customAssertionScore || "",
    explanationOfDrugResponse: defaults?.explanationOfDrugResponse || "",
    explanationOfOtherClinicalSignificance:
      defaults?.explanationOfOtherClinicalSignificance || "",
  });

  const [articles, setArticles] = useState([]);
  const [pubMedErrors, setPubMedErrors] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loaders, setLoaders] = useState(
    new Array(state.citation.length).fill(false)
  );
  const debounced = useCallback(
    debounce(
      (id, index) => {
        const newArticles = [...articles];
        const newErrors = [...pubMedErrors];
        const response = getPubmedArticles(id);
        response
          .then(({ results }) => {
            const article = results[0];
            const se = Object.keys(article || {}).length
              ? new Article(article)
              : null;
            newArticles[index] = se;
            setArticles(newArticles);
            newErrors[index] = null;
            setPubMedErrors(newErrors);
          })
          .catch((err) => {
            newArticles[index] = null;
            setArticles(newArticles);
            newErrors[index] = "Invalid PubMed id";
            setPubMedErrors(newErrors);
            console.error("Error inside clinical significance pubMed Id", err);
          })
          .finally(() => {
            setIsLoading(false);
            const newLoaders = [...loaders];
            for (let i = 0; i < newLoaders.length; i++) {
              newLoaders[i] = false;
            }
            setLoaders(newLoaders);
          });
      },
      1000,
      {
        leading: false,
        trailing: true,
      }
    ),
    [debounce, articles, pubMedErrors]
  );

  const findPubmedId = (e, index, forceId = null) => {
    // clear all loaders
    const newLoaders = [...loaders];

    const name = e ? e.target.name : null;
    const newErrors = [...pubMedErrors];
    const newArticles = [...articles];
    if (
      state.citation[index].id === "" ||
      state.citation[index].db !== "PubMed"
    ) {
      newLoaders[index] = false;
      setLoaders(newLoaders);
      newErrors[index] = null;
      setPubMedErrors(newErrors);
      newArticles[index] = null;
      setArticles(newArticles);
      setIsLoading(false);
      return;
    }
    if (name === "db") {
      setIsLoading(true);
      const referenceValue = state.citation[index].id;
      newLoaders[index] = true;
      setLoaders(newLoaders);
      debounced(referenceValue, index);
      return;
    }
    setIsLoading(true);
    const value = forceId || e.target.value;
    newLoaders[index] = true;
    setLoaders(newLoaders);
    debounced(value, index);
  };

  const initPubmedValidations = (id) => {
    const response = getPubmedArticles(id);
    return response;
  };

  useEffect(async () => {
    const res = await Promise.all(
      state.citation.map(async (value, index) => {
        if (state.citation[index].db !== "PubMed" || state.citation.id === "")
          return null;
        try {
          const result = await initPubmedValidations(value.id);
          return result;
        } catch (error) {
          return null;
        }
      })
    );

    const fetchedArticles = res.map((response) => {
      const data = response?.results?.[0] || null;
      const article = data;
      if (article) {
        return Object.keys(article || {}).length ? new Article(article) : null;
      }
      return null;
    });

    const newErrors = [...pubMedErrors];
    for (let i = 0; i < fetchedArticles.length; i++) {
      if (
        fetchedArticles[i] === null &&
        state.citation[i].id !== "" &&
        state.citation[i].db === "PubMed"
      ) {
        newErrors[i] = "Invalid pubmed id";
      }
    }
    setPubMedErrors(newErrors);
    setArticles([...articles, ...fetchedArticles]);
  }, []);

  useEffect(() => {
    onChageClinicalSign({ ...state });
  }, [state]);

  const handleAddClick = () => {
    setState({
      ...state,
      citation: [...state.citation, { db: "", id: "", url: "" }],
    });
    const newErrors = { ...errors };
    newErrors.clinicalSignificance.citation.push({
      db: validationErrors.emptyStringError,
      id: validationErrors.emptyStringError,
      url: validationErrors.emptyStringError,
    });
    setErrors(newErrors);
    const newTouched = { ...touched };
    newTouched.clinicalSignificance.citation.push({
      db: false,
      id: false,
      url: false,
    });
    setTouched(newTouched);
    const newPubmedArticles = [...articles];
    newPubmedArticles.push(null);
    setArticles(newPubmedArticles);
    setPubMedErrors([...pubMedErrors, null]);
    setLoaders([...loaders, false]);
  };
  const handleRemoveClick = (index) => {
    const list = [...state.citation];
    list.splice(index, 1);
    setState({ ...state, citation: list });

    const newErrors = { ...errors };
    newErrors.clinicalSignificance.citation.splice(index, 1);
    setErrors(newErrors);
    const newTouched = { ...touched };
    newTouched.clinicalSignificance.citation.splice(index, 1);
    setTouched(newTouched);
    const newPubmedErrors = [...pubMedErrors];
    const newArticles = [...articles];

    newArticles.splice(index, 1);
    newPubmedErrors.splice(index, 1);

    setArticles(newArticles);
    setPubMedErrors(newPubmedErrors);
  };

  const handleInputChange = (e, index) => {
    const { name, value } = e.target;
    const list = { ...state };
    list.citation[index][name] = value;
    setState({ ...list });
  };

  const onValueChange = (e, index = null) => {
    const { name, value } = e.target;
    if (name === "db" || name === "id" || name === "url") {
      handleInputChange(e, index);
    } else {
      const isNumericVariable = name === "customAssertionScore";
      const numericValue = value && isNumericVariable ? Number(value) : NaN;
      // eslint-disable-next-line no-unused-expressions
      isNaN(numericValue)
        ? setState({ ...state, [name]: value })
        : setState({ ...state, [name]: numericValue });
    }
    if (index >= 0) {
      if (value === "") {
        if (
          state.citation[index].id === "" &&
          state.citation[index].url === "" &&
          state.citation[index].db === ""
        ) {
          const newErrors = { ...errors };
          newErrors.clinicalSignificance.citation[index].url =
            validationErrors.emptyStringError;
          newErrors.clinicalSignificance.citation[index].id =
            validationErrors.emptyStringError;
          newErrors.clinicalSignificance.citation[index].db =
            validationErrors.emptyStringError;
          return;
        }
        if (name === "db" && state.citation[index].id === "") {
          const newErrors = { ...errors };
          newErrors.clinicalSignificance.citation[index].url =
            validationErrors.emptyStringError;
          setErrors(newErrors);
        } else if (name === "id" && state.citation[index].db === "") {
          const newErrors = { ...errors };
          newErrors.clinicalSignificance.citation[index].url =
            validationErrors.emptyStringError;
          setErrors(newErrors);
        } else if (
          name === "url" &&
          state.citation[index].db === "" &&
          state.citation[index].id === ""
        ) {
          const newErrors = { ...errors };
          newErrors.clinicalSignificance.citation[index].db = "";
          newErrors.clinicalSignificance.citation[index].id = "";
          setErrors(newErrors);
        }
        return;
      }
      if ((name === "db" || name === "id") && value !== "") {
        const newErrors = { ...errors };

        newErrors.clinicalSignificance.citation[index].url = "";
        if (
          name === "db" &&
          newErrors.clinicalSignificance.citation[index].id === "" &&
          state.citation[index].id === ""
        ) {
          newErrors.clinicalSignificance.citation[index].id =
            validationErrors.emptyStringError;
          newErrors.clinicalSignificance.citation[index].db = "";
        } else if (
          name === "id" &&
          newErrors.clinicalSignificance.citation[index].db === "" &&
          state.citation[index].db === ""
        ) {
          newErrors.clinicalSignificance.citation[index].db =
            validationErrors.emptyStringError;
          newErrors.clinicalSignificance.citation[index].id = "";
        }
        setErrors(newErrors);
      } else if (
        name === "url" &&
        value !== "" &&
        (errors.clinicalSignificance.citation[index].id !== "" ||
          errors.clinicalSignificance.citation[index].db !== "")
      ) {
        const newErrors = { ...errors };
        newErrors.clinicalSignificance.citation[index].id = "";
        newErrors.clinicalSignificance.citation[index].db = "";
        setErrors(newErrors);
      }
    }
  };

  return (
    <div className="tw-pt-8 tw-pb-8">
      <div className="tw-text-3xl">Clinical Significance</div>

      <div className="tw-text-sm">{Descriptions.citationClinSign}</div>
      <div className="tw-pt-6 tw-px-2 tw-flex tw-w-11/12">
        <div className="tw-w-4/12 tw-mr-4">
          <Label text="Database *" />
        </div>
        <div className="tw-w-4/12 tw-mr-4">
          <Label text="Citation Id *" />
        </div>
        <div className="tw-w-4/12 tw-mr-4">
          <Label text="Citation Url *" />
        </div>
      </div>

      {state.citation.map((x, index) => {
        const key = `${index}-${state.citation}`;
        return (
          <div key={key}>
            <div className="tw-flex tw-flex-wrap tw-px-2 tw-py-4 tw-bg-gray-100">
              <div className="tw-flex tw-w-11/12">
                <FormGroup className="tw-w-4/12 tw-mr-4" hasPadding={false}>
                  <Select
                    disabled={x.url}
                    name="db"
                    onBlur={(e) => handleBlur(e, index)}
                    id={`clinicalSignificancedb-${index}`}
                    onChange={(e) => {
                      onValueChange(e, index);
                      findPubmedId(e, index);
                    }}
                    value={x.db}
                  >
                    <option value="" />
                    {DbOptions.map((value) => (
                      <option key={`${value}-${state.citation}`} value={value}>
                        {value}
                      </option>
                    ))}
                  </Select>
                  {touched.clinicalSignificance.citation[index].db &&
                  errors.clinicalSignificance.citation[index].db ? (
                    <InputHelperText type="error">
                      {validationErrors.emptyStringError}
                    </InputHelperText>
                  ) : (
                    <React.Fragment />
                  )}
                </FormGroup>
                <FormGroup className="tw-w-4/12 tw-mr-4" hasPadding={false}>
                  <div className="tw-flex">
                    <Input
                      disabled={x.url}
                      type="text"
                      id={`clinicalSignificanceid-${index}`}
                      name="id"
                      value={x.id}
                      onBlur={(e) => handleBlur(e, index)}
                      onChange={(e) => {
                        onValueChange(e, index);
                        findPubmedId(e, index);
                      }}
                    />
                    {state.citation[index].db === "PubMed" &&
                      loaders[index] === true && (
                        <button type="button" disabled={true}>
                          {loaders[index] === true ? <Loading /> : null}
                        </button>
                      )}
                  </div>
                  {pubMedErrors[index] !== null &&
                    typeof pubMedErrors[index] !== "undefined" && (
                      <small className="form-text text-danger">
                        {pubMedErrors[index]}
                      </small>
                    )}
                  {touched.clinicalSignificance.citation[index].id &&
                  errors.clinicalSignificance.citation[index].id &&
                  !pubMedErrors[index] ? (
                    <InputHelperText type="error">
                      {validationErrors.emptyStringError}
                    </InputHelperText>
                  ) : (
                    <React.Fragment />
                  )}
                  <div className="tw-mt-2">
                    <ArticleCitation article={articles[index]} />
                  </div>
                </FormGroup>
                <FormGroup className="tw-w-4/12 tw-mr-4" hasPadding={false}>
                  <Input
                    disabled={x.db || x.id}
                    type="text"
                    name="url"
                    id={`clinicalSignificanceurl-${index}`}
                    onBlur={(e) => handleBlur(e, index)}
                    value={x.url}
                    onChange={(e) => onValueChange(e, index)}
                  />
                  {touched.clinicalSignificance.citation[index].url &&
                  errors.clinicalSignificance.citation[index].url ? (
                    <InputHelperText type="error">
                      {validationErrors.emptyStringError}
                    </InputHelperText>
                  ) : (
                    <React.Fragment />
                  )}
                </FormGroup>
              </div>
              <div className="tw-flex tw-align-middle tw-items-center tw-justify-center tw-w-1/12">
                {state.citation.length !== 1 && (
                  <FontAwesomeIcon
                    style={loaders[index] ? disabledStyle : {}}
                    icon={faTrash}
                    onClick={() => handleRemoveClick(index)}
                    size="lg"
                    fixedWidth={true}
                    className="tw-px-1 tw-text-red-600 tw-cursor-pointer"
                  />
                )}
              </div>
            </div>
            {state.citation.length - 1 === index && (
              <div className="tw-flex tw-m-4 tw-justify-center">
                <button
                  type="button"
                  onClick={() => handleAddClick(index)}
                  className="btn btn-success"
                  disabled={
                    ((state.citation[state.citation.length - 1].db === "" ||
                      state.citation[state.citation.length - 1].id === "") &&
                      state.citation[state.citation.length - 1].url === "") ||
                    isLoading
                  }
                >
                  <FontAwesomeIcon
                    icon={faPlus}
                    fixedWidth={true}
                    className="tw-px-1 tw-text-white"
                  />
                  Add Citation
                </button>
              </div>
            )}
          </div>
        );
      })}

      <FormGroup className="tw-w-6/12">
        <Label
          htmlFor="ClinicalSignificanceDescription"
          text="Significance Description"
          isRequired={true}
        />
        <Select
          onChange={(e) => onValueChange(e)}
          onBlur={handleBlur}
          name="clinicalSignificanceDescription"
          value={state.clinicalSignificanceDescription}
        >
          <option value="" />
          {ClinicalSignificanceDescriptionOptions.map(({ value, label }) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </Select>
        {!touched.clinicalSignificance?.clinicalSignificanceDescription ||
        !errors.clinicalSignificance?.clinicalSignificanceDescription ? (
          <InputHelperText>
            {Descriptions.clinicalSignificanceDescription}
            <HelperText
              customText={Descriptions.clinicalSignificanceDescription}
            />
          </InputHelperText>
        ) : (
          <InputHelperText type="error">
            {errors?.clinicalSignificance.clinicalSignificanceDescription}
          </InputHelperText>
        )}
      </FormGroup>
      <FormGroup className="tw-w-6/12">
        <Label text="Comment" />
        <Textarea
          type="text"
          name="comment"
          value={state.comment}
          onChange={(e) => onValueChange(e)}
        />
        <InputHelperText>
          Free text describing the rationale for the clinical significance.
        </InputHelperText>
      </FormGroup>
      <FormGroup className="tw-w-6/12">
        <Label text="Assertion Score" />
        <Input
          type="text"
          name="customAssertionScore"
          placeholder=""
          value={state.customAssertionScore}
          onChange={(e) => onValueChange(e)}
          onBlur={handleBlur}
        />
        {!touched?.clinicalSignificance?.customAssertionScore ||
        !errors?.clinicalSignificance?.customAssertionScore ? (
          <InputHelperText>
            The final score, or point value, calculated when the assertion
            method uses a point-based scoring system, e.g. Germline Variant
            Classification/ClinGen CNV Guidelines, 2019 (PMID: 31690835). The
            assertion method must also be provided.
          </InputHelperText>
        ) : (
          <InputHelperText type="error">
            {errors?.clinicalSignificance.customAssertionScore}
          </InputHelperText>
        )}
      </FormGroup>
      <FormGroup className="tw-w-6/12">
        <Label text="Explanation of drug response" />
        <Input
          type="text"
          name="explanationOfDrugResponse"
          placeholder=""
          value={state.explanationOfDrugResponse}
          onChange={(e) => onValueChange(e)}
        />
        <InputHelperText>
          {Descriptions.explanationOfDrugResponse}
        </InputHelperText>
      </FormGroup>

      <FormGroup className="tw-w-6/12">
        <Label text="Explanation of other clinical significance" />
        <Input
          type="text"
          name="explanationOfOtherClinicalSignificance"
          placeholder=""
          value={state.explanationOfOtherClinicalSignificance}
          onChange={(e) => onValueChange(e)}
        />
        <InputHelperText>
          {Descriptions.explanationOfOtherClinicalSignificance}
        </InputHelperText>
      </FormGroup>

      <FormGroup className="tw-w-6/12">
        <Label htmlFor="dateLastEvaluated" text="Last day of evaluation" />
        <Input
          type="text"
          name="dateLastEvaluated"
          placeholder="YYYY-MM-DD"
          value={state.dateLastEvaluated}
          onChange={(e) => onValueChange(e)}
          onBlur={handleBlur}
        />
        {!touched?.clinicalSignificance?.dateLastEvaluated ||
        !errors?.clinicalSignificance?.dateLastEvaluated ? (
          <InputHelperText>
            The date that the clinical significance was last evaluated by the
            submitter (not the date the phenotype of the patient was evaluated),
            equivalent to Date last evaluated in the submission spreadsheet. Use
            the format yyyy-mm-dd. If only month/year is known, use the first
            day of the month. If only year is known, use Jan. 1
          </InputHelperText>
        ) : (
          <InputHelperText type="error">
            {errors?.clinicalSignificance.dateLastEvaluated}
          </InputHelperText>
        )}
      </FormGroup>
      <FormGroup className="tw-w-6/12">
        <Label htmlFor="description" text="Mode Of Inheritance" />
        <Select
          onChange={(e) => onValueChange(e)}
          name="modeOfInheritance"
          value={state.modeOfInheritance}
        >
          <option value="" />
          {ModeofInhertinceOptions.map((value) => (
            <option key={value} value={value}>
              {value}
            </option>
          ))}
        </Select>
        <InputHelperText>
          {Descriptions.modeOfInheritance}
          <HelperText customText={Descriptions.modeOfInheritanceDetails} />{" "}
        </InputHelperText>
      </FormGroup>
      {recordStatus !== "novel" && (
        <ClinvarAccession
          onChange={onClinvarAccesion}
          formStatus={formStatus}
          fetchedClinvarAccession={fetchedClinvarAccession}
          statusCode={statusCode}
        />
      )}
    </div>
  );
};

ClinicalSignificance.propTypes = {
  statusCode: PropTypes.number,
  onChageClinicalSign: PropTypes.func.isRequired,
  recordStatus: PropTypes.string,
  onClinvarAccesion: PropTypes.func.isRequired,
  fetchedClinvarAccession: PropTypes.string,
  defaults: ClinicalSignificanceFieldsType,
  handleBlur: PropTypes.func.isRequired,
  setErrors: PropTypes.func.isRequired,
  setTouched: PropTypes.func.isRequired,
  formStatus: PropTypes.string,
  errors: PropTypes.objectOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({}),
      PropTypes.arrayOf(PropTypes.shape({})),
    ])
  ).isRequired,
  touched: PropTypes.objectOf(
    PropTypes.oneOfType([
      PropTypes.bool,
      PropTypes.shape({}),
      PropTypes.arrayOf(PropTypes.shape({})),
    ])
  ).isRequired,
};

ClinicalSignificance.defaultProps = {
  defaults: null,
  statusCode: 0,
  recordStatus: "",
  fetchedClinvarAccession: "",
  formStatus: null,
};

export default ClinicalSignificance;
